import type { Values } from '@ps/web-core-intl';
import type { Alias } from '@ps/web-core-store';
import { generatePath } from '@ps/web-core-utils';
import { useTrackers } from '@ps/web-shared-analytics';
import type { StarsButtonProps } from '@ps/web-shared-components';
import { RawButton, StarsButton, useNavigate, useTrackingLabel } from '@ps/web-shared-components';
import classNames from 'clsx';
import React from 'react';
import type { MouseEventHandler } from 'react';
import { useSelector } from 'react-redux';

import Arrow from '@swf/core/assets/icons/arrow.svg';
import useWizardContext from '@swf/core/hooks/useWizardContext';
import { select } from '@swf/core/store';

import styles from './Button.scss';

export interface Props
  extends Omit<StarsButtonProps, 'children' | 'values'>,
    React.PropsWithChildren {
  align?: 'left' | 'right';
  alias?: Alias;
  values?: Values;
  selected?: boolean;
  firstVariant?: 'help' | 'dropdown';
  startIcon?: StarsButtonProps['icon'];
  endIcon?: StarsButtonProps['icon'];
  firstButtonIcon?: StarsButtonProps['icon'];
  isFullWidth?: boolean;
  endIconFloatRight?: boolean;
}

const Button: React.FC<Props> = ({
  translation,
  trackingValue: value,
  values,
  variant = 'primary',
  size,
  className,
  onClick,
  children,
  selected,
  align,
  alias,
  loading,
  type,
  disabled,
  endIcon,
  startIcon,
  firstButtonIcon,
  firstVariant,
  isFullWidth = false,
  endIconFloatRight = false,
  ...rest
}) => {
  const { buttonPressed } = useTrackers();
  const buttonName = useTrackingLabel(translation, values);
  const navigate = useNavigate();
  const aliases = useSelector(select.cms.getAliases);
  const isHelp = firstVariant === 'help';
  const iconButton = !translation;
  const handleClick: MouseEventHandler = (event) => {
    buttonPressed({ buttonName, value });
    alias ? aliases && navigate(generatePath(aliases[alias] as string, {})) : onClick?.(event);
  };
  const {
    data: { cmsHeaderAndGridLayout }
  } = useWizardContext();

  return children ? (
    <RawButton
      {...rest}
      type={type}
      icon={iconButton}
      variant={variant}
      onClick={handleClick}
      className={classNames(
        className,
        styles[firstVariant as keyof typeof styles],
        styles[align as keyof typeof styles],
        {
          [styles.root]: !firstVariant,
          [styles.inlineWrapper]: !isFullWidth && cmsHeaderAndGridLayout
        }
      )}
    >
      {children}
    </RawButton>
  ) : (
    <div
      className={classNames(styles.defaultWrapper, {
        [styles.primaryWrapper]: variant === 'primary',
        [styles.secondaryWrapper]: variant === 'secondary',
        [styles.inlineWrapper]: !isFullWidth && cmsHeaderAndGridLayout
      })}
    >
      {firstButtonIcon}
      <StarsButton
        type={type}
        size={variant !== 'text' ? size : 'small'}
        translation={translation}
        variant={variant}
        loading={loading}
        onClick={handleClick}
        disabled={disabled}
        values={values}
        iconPosition={isHelp || endIcon ? 'after' : startIcon ? 'before' : undefined}
        icon={isHelp ? <Arrow className={styles.arrow} /> : endIcon || startIcon}
        className={classNames(
          className,
          styles[firstVariant as keyof typeof styles],
          styles[align as keyof typeof styles],
          {
            [styles.root]: !firstVariant,
            [styles.startIcon]: startIcon,
            [styles.endIconFloatRight]: endIconFloatRight
          }
        )}
        {...rest}
      />
    </div>
  );
};

export default Button;
